// Custom-measure sync with Giraffe's own analytics app (app id "1",
// `public.counter`) — the "Spreadsheet" tab, "Custom Calculations" category.
//
// Investigated live before any of this was written (the brief that asked for
// it was explicit that the shape must not be guessed): a measure's `formula`
// is a tiny arithmetic expression over COMPONENT LETTERS (A, B, C, D, skipping
// E — it parses as Euler's number — then F, G, …), and `components[]` is an
// array of TYPED reference objects, never bare field-name strings. The
// translation from a gTables expression into that shape lives in
// `data/expression.ts` (`toMeasureFormula`); this file only owns the read and
// the read-merge-write.
//
// The write goes through `updateProjectApp('1', wholeApp)` — the same call
// `adapter.ts`'s `writeUsages`/`removeUsageFields` already make against this
// exact app id, for the same reason: it is the only RPC `@gi-nx/iframe-sdk`
// exposes for this, and it replaces the record WHOLE. So every write here
// reads the live app fresh, `structuredClone`s it (the SDK rejects a mutated
// live object), touches ONLY `public.counter.tabs/categories/measures`, and
// sends the rest back untouched.

import { readState, updateProjectApp } from './adapter';
import { toMeasureFormula } from '@/data/expression';

export const SPREADSHEET_TAB_LABEL = 'Spreadsheet';
export const CUSTOM_CALCULATIONS_CATEGORY_LABEL = 'Custom Calculations';

export interface GiraffeTab {
  id: string;
  label: string;
  color?: string;
}

export interface GiraffeCategory {
  id: string;
  tabs: string[];
  label: string;
  color?: string;
  isClosed?: boolean;
  subcategorizeByUsage?: boolean;
  displays?: unknown[];
}

export interface GiraffeMeasureComponent {
  /** The field (for `rawProperty`) or another measure's id (for `lineItem`). */
  id?: string;
  type: 'rawProperty' | 'site' | 'lineItem' | 'subtotal';
  /**
   * Confirmed live (2026-10-24): `rawProperty` -> `'property'`; `lineItem` ->
   * `'measure'` | `'derivativeLineItem'` | `'derivativeCategory'`; `subtotal`
   * -> `'unitInCategory'`. See `giraffe/nativeMeasures.ts`.
   */
  subType?: string;
  siteKey?: string;
  /** Present on a `subtotal` component — which category it sums. */
  categoryId?: string;
  unitId?: string;
}

export interface FeatureFilterClause {
  property: string;
  filter: { type: string; operator: string; value: unknown };
}

export interface GiraffeFeatureFilter {
  operator: 'and' | 'or';
  filters: FeatureFilterClause[];
}

/** A measure carries only the fields it uses — every field here is optional bar the few every measure has. */
export interface GiraffeMeasure {
  id: string;
  name: string;
  formula: string;
  operation: 'SUM' | 'CONSTANT' | 'AVERAGE';
  components: GiraffeMeasureComponent[];
  categoryId: string;
  unitId: string;
  precision?: number;
  isBold?: boolean;
  isHidden?: boolean;
  tags?: string[];
  detail?: string;
  appliedToUsages?: unknown[];
  featureFilter?: GiraffeFeatureFilter;
  rate?: { match: string; unitId: string };
  quantity?: { match: string; unitId: string };
}

/**
 * Everything the Analytics PANEL uses to decide display order — confirmed
 * live (2026-10-24) against this project's real `counter.display`, and
 * against how the Cashflow app (`cashflow` repo, `buildMeasureIdsForTab` /
 * `sortByExternalOrder`) already reproduces that same order for its own
 * rows: a category's position comes from `tabCategoryOrder[tabId]`, itself
 * ordered by `tabOrder`; a measure's position within its category comes from
 * `categoryUsageRowOrders[categoryId].__COMBINED` (a mix of measure ids and
 * unit-subtotal ids); a split column's position comes from `columnOrder` —
 * by LABEL ("Scenario 1"), not by groupKey.
 */
export interface GiraffeDisplay {
  tabOrder?: string[];
  tabCategoryOrder?: Record<string, string[]>;
  categoryUsageRowOrders?: Record<string, { __COMBINED?: string[] }>;
  columnOrder?: string[];
}

interface Counter {
  tabs: Record<string, GiraffeTab>;
  categories: Record<string, GiraffeCategory>;
  measures: GiraffeMeasure[];
  units?: Record<string, unknown>;
  display?: GiraffeDisplay;
  groupBy?: string[];
}

/** Everything the Analytics sheet needs beyond `getAnalyticsResult()` itself: tab/category labels, display order, and full measure definitions (featureFilter included — `getAnalyticsResult()`'s own measure objects are a reduced shape without it). */
export interface AnalyticsLayout {
  tabs: Readonly<Record<string, GiraffeTab>>;
  categories: Readonly<Record<string, GiraffeCategory>>;
  display: GiraffeDisplay;
  measuresById: ReadonlyMap<string, GiraffeMeasure>;
}

function readCore(): Record<string, unknown> | null {
  const apps = readState<Record<string, unknown>>('projectAppsByAppID');
  return (apps?.['1'] as Record<string, unknown> | undefined) ?? null;
}

function readCounter(core: Record<string, unknown> | null): Counter | null {
  const pub = core?.public as Record<string, unknown> | undefined;
  return (pub?.counter as Counter | undefined) ?? null;
}

function findSpreadsheetCategory(counter: Counter): { tab: GiraffeTab; category: GiraffeCategory } | null {
  const tab = Object.values(counter.tabs ?? {}).find((t) => t.label === SPREADSHEET_TAB_LABEL);
  if (!tab) return null;
  const category = Object.values(counter.categories ?? {}).find(
    (c) => c.label === CUSTOM_CALCULATIONS_CATEGORY_LABEL && c.tabs.includes(tab.id)
  );
  return category ? { tab, category } : null;
}

/**
 * Every measure currently under Spreadsheet / Custom Calculations, read
 * straight off broadcast state — no round trip, so an edit made anywhere in
 * Giraffe (by this app in a previous session, or by someone in Giraffe's own
 * analytics editor) reaches the Measures tab on the next broadcast.
 */
export function readCustomCalculationMeasures(): GiraffeMeasure[] {
  const counter = readCounter(readCore());
  if (!counter) return [];
  const loc = findSpreadsheetCategory(counter);
  if (!loc) return [];
  return (counter.measures ?? []).filter((m) => m.categoryId === loc.category.id);
}

/**
 * Every measure in the project's analytics app, in every category — not
 * just the gTables-owned Spreadsheet / Custom Calculations one. Feeds
 * `giraffe/nativeMeasures.ts`'s `resolveNativeMeasures`, which is what
 * actually turns a measure into a Features-sheet column (or decides it
 * can't, and says why — see that file's header comment for how the
 * field-naming and recursive-reference blockers this function used to sit
 * behind got solved).
 */
export function readAllMeasures(): GiraffeMeasure[] {
  const counter = readCounter(readCore());
  return counter?.measures ?? [];
}

/** Same read as `readAllMeasures`, plus what the Analytics sheet needs to label a Tab/Category column and order everything the way Analytics itself does — or `null` before the project has broadcast anything. */
export function readAnalyticsLayout(): AnalyticsLayout | null {
  const counter = readCounter(readCore());
  if (!counter) return null;
  return {
    tabs: counter.tabs ?? {},
    categories: counter.categories ?? {},
    display: counter.display ?? {},
    measuresById: new Map((counter.measures ?? []).map((m) => [m.id, m])),
  };
}

function newGiraffeId(): string {
  // Any unique string works as an id in this record — Giraffe does not parse
  // or validate the format, only uniqueness as an object key / array member.
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `m${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
}

export interface MeasureSyncResult {
  ok: boolean;
  reason?: string;
}

/**
 * Make the Spreadsheet / Custom Calculations category hold exactly `wanted` —
 * one measure per distinct gTables expression, keyed by its literal text as
 * the measure's `name` (the same text the Measures tab heads its column
 * with). Adds what is missing, updates what changed, removes what is no
 * longer wanted. Never touches a measure in a different category, or any
 * other part of the app.
 *
 * `wanted` empty removes the whole category (and the tab, if it is then
 * empty) rather than leaving a category with nothing in it sitting in
 * Giraffe's own analytics UI.
 */
export async function syncCustomCalculations(wanted: ReadonlySet<string>): Promise<MeasureSyncResult> {
  const core = readCore();
  if (!core) return { ok: false, reason: 'The core Giraffe app state is not readable yet.' };

  const app = structuredClone(core) as Record<string, unknown>;
  const pub = (app.public ??= {}) as Record<string, unknown>;
  const counter = (pub.counter ??= { tabs: {}, categories: {}, measures: [] }) as Counter;
  counter.tabs ??= {};
  counter.categories ??= {};
  counter.measures ??= [];

  const existingLoc = findSpreadsheetCategory(counter);
  const existing = existingLoc
    ? new Map(counter.measures.filter((m) => m.categoryId === existingLoc.category.id).map((m) => [m.name, m] as const))
    : new Map<string, GiraffeMeasure>();

  if (wanted.size === 0) {
    if (!existingLoc) return { ok: true }; // nothing to do, nothing to undo
    counter.measures = counter.measures.filter((m) => m.categoryId !== existingLoc.category.id);
    delete counter.categories[existingLoc.category.id];
    // The tab only ever holds this one category today; take it down too once
    // empty, rather than leaving an empty "Spreadsheet" tab behind.
    const stillUsed = Object.values(counter.categories).some((c) => c.tabs.includes(existingLoc.tab.id));
    if (!stillUsed) delete counter.tabs[existingLoc.tab.id];
    const res = await updateProjectApp('1', app);
    return res.ok ? { ok: true } : { ok: false, reason: res.reason };
  }

  let tab = existingLoc?.tab ?? Object.values(counter.tabs).find((t) => t.label === SPREADSHEET_TAB_LABEL);
  if (!tab) {
    tab = { id: newGiraffeId(), label: SPREADSHEET_TAB_LABEL, color: '#FFFFFF' };
    counter.tabs[tab.id] = tab;
  }
  let category = existingLoc?.category;
  if (!category) {
    category = {
      id: newGiraffeId(),
      tabs: [tab.id],
      label: CUSTOM_CALCULATIONS_CATEGORY_LABEL,
      color: '#9aa5b1',
      isClosed: false,
      subcategorizeByUsage: false,
    };
    counter.categories[category.id] = category;
  }

  for (const expr of wanted) {
    const { formula, fields } = toMeasureFormula(expr);
    const components: GiraffeMeasureComponent[] = fields.map((f) => ({ id: f, type: 'rawProperty', subType: 'property' }));
    const prior = existing.get(expr);
    if (prior) {
      prior.formula = formula;
      prior.components = components;
      continue;
    }
    counter.measures.push({
      id: newGiraffeId(),
      name: expr,
      detail: 'Declared from a gTables formula.',
      formula,
      operation: 'SUM',
      components,
      categoryId: category.id,
      unitId: 'UNITLESS',
      precision: 2,
      isBold: false,
      isHidden: false,
      tags: [],
      appliedToUsages: [],
    });
  }
  counter.measures = counter.measures.filter((m) => m.categoryId !== category!.id || wanted.has(m.name));

  const res = await updateProjectApp('1', app);
  return res.ok ? { ok: true } : { ok: false, reason: res.reason };
}
